import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function WoPaymentDialog({ wo, onClose, onDone }) {
  const [form, setForm] = useState({ mode: "percentage", amount: 0, percentage: 100 });
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(null);
  const sending = useRef(false);

  useEffect(() => {
    if (wo) { setForm(wo.preset || { mode: "percentage", amount: 0, percentage: 100 }); keyRef.current = newKey(); }
  }, [wo?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!wo || sending.current) return;
    sending.current = true; setBusy(true);
    try {
      const payload = form.mode === "amount" ? { amount: Number(form.amount) } : { percentage: Number(form.percentage) };
      await api.post(`/work-orders/${wo.id}/invoice-partial`, { ...payload, request_id: keyRef.current });
      toast.success("Invoice generated for the selected portion");
      onClose(); onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    sending.current = false; setBusy(false);
  };

  return (
    <Dialog open={!!wo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[1.25rem] max-w-md" data-testid="completed-payment-dialog">
        {wo && (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl text-royal-800">Work order completed</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3 text-sm">
              <div className="text-ink/80">«{wo.title}» has been completed. Do you want to process it for payment now?</div>
              <div className="flex gap-2">
                <button type="button" data-testid="pay-mode-pct" onClick={() => setForm({ ...form, mode: "percentage" })} className={`flex-1 px-3 py-2 rounded-full text-xs ${form.mode === "percentage" ? "bg-royal-800 text-cream" : "border border-black/15"}`}>By percentage</button>
                <button type="button" data-testid="pay-mode-amt" onClick={() => setForm({ ...form, mode: "amount" })} className={`flex-1 px-3 py-2 rounded-full text-xs ${form.mode === "amount" ? "bg-royal-800 text-cream" : "border border-black/15"}`}>By amount</button>
              </div>
              {form.mode === "percentage" ? (
                <div><label className="label-caps block mb-1">Percentage to invoice now</label>
                  <Input data-testid="pay-percentage" type="number" min={1} max={100} value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} className="rounded-lg border-black/15" />
                  <div className="text-xs text-neutral-500 font-mono-tight mt-1">≈ ₹{Math.round((Number(form.percentage) || 0) * (wo.rate || 0) / 100).toLocaleString()}</div>
                </div>
              ) : (
                <div><label className="label-caps block mb-1">Amount (INR) — WO total ₹{(wo.rate || 0).toLocaleString()}</label>
                  <Input data-testid="pay-amount" type="number" min={1} max={wo.rate} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-lg border-black/15" />
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" data-testid="pay-skip" onClick={onClose} className="flex-1 rounded-full border border-black/15 py-2.5 text-sm font-semibold hover:bg-cream-2">Not now</button>
                <button type="submit" data-testid="pay-confirm" disabled={busy} className="flex-1 rounded-full bg-royal-800 text-cream py-2.5 text-sm font-semibold disabled:opacity-50">{busy ? "Processing…" : "Generate invoice"}</button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
